import React from 'react';
import { BackendNode } from './types';
import { VIEW_W, LEFT_X, RIGHT_X, CENTER_X, CENTER_R, calcViewH, satY } from './layout';
import { SatNode, CenterNode, isReportMember } from './Nodes';
import Legend from './Legend';

interface OverviewSvgProps {
    centerNode: BackendNode;
    leftNodes: BackendNode[];
    rightNodes: BackendNode[];
    typeColors: Map<string, string>;
    hoveredId: number | null;
    reportLensActive?: boolean;
    reportMemberUids?: ReadonlySet<string>;
    onHover: (id: number | null) => void;
    onClick: (node: BackendNode) => void;
}

export default function OverviewSvg({
    centerNode,
    leftNodes,
    rightNodes,
    typeColors,
    hoveredId,
    reportLensActive = false,
    reportMemberUids,
    onHover,
    onClick,
}: OverviewSvgProps) {
    const h = calcViewH(leftNodes.length, rightNodes.length);
    const cy = h / 2;

    return (
        <svg viewBox={`0 0 ${VIEW_W} ${h}`} width="100%" overflow="visible" style={{ display: 'block' }}>
            <Legend typeColors={typeColors} reportLensActive={reportLensActive} />

            {leftNodes.map((n, i) => {
                const sy = satY(i, leftNodes.length, h);
                const mx = (CENTER_X + LEFT_X) / 2;
                return (
                    <path
                        key={n.llm_learning_node_id}
                        d={`M ${CENTER_X} ${cy} C ${mx} ${cy}, ${mx} ${sy}, ${LEFT_X} ${sy}`}
                        fill="none"
                        stroke="#b8c0cc"
                        strokeWidth={1.5}
                    />
                );
            })}

            {rightNodes.map((n, i) => {
                const sy = satY(i, rightNodes.length, h);
                const mx = (CENTER_X + RIGHT_X) / 2;
                return (
                    <path
                        key={n.llm_learning_node_id}
                        d={`M ${CENTER_X} ${cy} C ${mx} ${cy}, ${mx} ${sy}, ${RIGHT_X} ${sy}`}
                        fill="none"
                        stroke="#b8c0cc"
                        strokeWidth={1.5}
                    />
                );
            })}

            {leftNodes.map((n, i) => (
                <SatNode
                    key={n.llm_learning_node_id}
                    node={n}
                    cx={LEFT_X}
                    cy={satY(i, leftNodes.length, h)}
                    labelSide="left"
                    hovered={hoveredId === n.llm_learning_node_id}
                    inReport={isReportMember(n, reportMemberUids)}
                    onHover={onHover}
                    onClick={onClick}
                />
            ))}

            {rightNodes.map((n, i) => (
                <SatNode
                    key={n.llm_learning_node_id}
                    node={n}
                    cx={RIGHT_X}
                    cy={satY(i, rightNodes.length, h)}
                    labelSide="right"
                    hovered={hoveredId === n.llm_learning_node_id}
                    inReport={isReportMember(n, reportMemberUids)}
                    onHover={onHover}
                    onClick={onClick}
                />
            ))}

            {/* The host root is outlined whenever a lens is on (prototype's `reportActive: !!rep`
                for the center node): the report reads from this model by construction, whether or
                not the membership payload spells the root's own uid out. */}
            <CenterNode
                node={centerNode}
                cx={CENTER_X}
                cy={cy}
                r={CENTER_R}
                hovered={hoveredId === centerNode.llm_learning_node_id}
                inReport={reportLensActive}
                onHover={onHover}
                onClick={onClick}
            />
        </svg>
    );
}
